/**
 * Folhas A4 a partir dos lotes e dos setores escolhidos.
 *
 * Uma folha = um lote × um setor, SEMPRE um papel só (decisão do Igor,
 * 23/10: "um papel por lote"; não existe folha de continuação). Quando o
 * conteúdo não cabe, a própria folha se compacta em degraus (ajusteDaFolha,
 * medido no DOM pela tela). Lote com mais de 10 ambientes não gera as folhas
 * dos setores: a tela avisa para dividir o lote na planilha.
 * Ordem: lote a lote, e dentro do lote o fluxo da fábrica (Compras,
 * Máquinas, Marcenaria, Terceiros, Acabamento, Expedição).
 */
import { F } from "../gerencialFields";
import { lerData, lerItem, lerManual, lerNumero, lerTexto, VAZIO } from "./celulas";
import { dataDoLote, entregaDoLote } from "./lotes";
import { CARREGAMENTO, SETORES, TIPO_COLUNA as T } from "./setores";

export const LINHAS_POR_FOLHA = 10;
/** Limite de ambientes por lote para as folhas dos setores (decisão do Igor, 23/10). */
export const MAXIMO_DE_AMBIENTES_POR_FOLHA = LINHAS_POR_FOLHA;

/** Lote com até 10 ambientes cabe num papel por setor. */
export function loteCabeNumaFolha(lote) {
  return lote.ambientes.length <= MAXIMO_DE_AMBIENTES_POR_FOLHA;
}

function celula(coluna, ambiente, lote) {
  const linha = ambiente.linha;
  switch (coluna.tipo) {
    case T.CLIENTE: return lerTexto(ambiente.cliente);
    case T.AMBIENTE: return lerTexto(ambiente.nome);
    case T.LOTE: return lerTexto(lote.numero);
    case T.PROJETO: return lerTexto(ambiente.projeto);
    case T.PRAZO: return lerNumero(ambiente.prazoDias);
    case T.MDF: return lerNumero(linha[F.MDF_M2]);
    case T.PECAS: return lerNumero(linha[F.PECAS_UN]);
    case T.DATA:
    case T.LIMITE:
    case T.ENTREGA: return lerData(linha[coluna.campo]);
    case T.MANUAL: return lerManual(linha[coluna.campo]);
    case T.ITEM: return lerItem(linha[coluna.campo]);
    case T.ITEM_DESCRITO: return lerItem(linha[coluna.campo], linha[coluna.campoDescricao]);
    default: return VAZIO;
  }
}

function montarLinha(setor, ambiente, lote) {
  return {
    chave: `${ambiente.projeto}|${ambiente.nome}`,
    celulas: Object.fromEntries(setor.colunas.map((coluna) => [coluna.id, celula(coluna, ambiente, lote)])),
  };
}

function itensDoSetor(setor, linhas) {
  const colunas = setor.colunas.filter((coluna) => coluna.tipo === T.ITEM || coluna.tipo === T.ITEM_DESCRITO);
  let itens = 0;
  let ambientes = 0;
  linhas.forEach((linha) => {
    const comQuantidade = colunas.filter((coluna) => linha.celulas[coluna.id].tipo === "qtd").length;
    itens += comQuantidade;
    if (comQuantidade > 0) ambientes += 1;
  });
  return { itens, ambientes };
}

/** Setor sem nenhum item no lote (ex.: nada de Terceiros) não gera folha. */
export function setorSeAplica(setor, lote) {
  if (!setor.exigeItens) return true;
  if (!setor.colunas) return true;
  const linhas = lote.ambientes.map((ambiente) => montarLinha(setor, ambiente, lote));
  return itensDoSetor(setor, linhas).itens > 0;
}

function cabecalho(setor, lote, linhas) {
  const resumo = setor.exigeItens ? itensDoSetor(setor, linhas) : null;
  return {
    etiqueta: lote.etiqueta,
    otimizacao: lote.otimizacao,
    totalM2: lote.totalM2,
    totalPecas: lote.totalPecas,
    totalAmbientes: lote.ambientes.length,
    itens: resumo,
    limite: dataDoLote(lote, setor.campoLimite),
    entrega: entregaDoLote(lote),
  };
}

function folhaDoSetor(setor, lote) {
  const linhas = lote.ambientes.map((ambiente) => montarLinha(setor, ambiente, lote));
  return {
    chave: `${lote.chave}:${setor.id}`,
    tipo: "setor",
    setor,
    lote,
    cabecalho: cabecalho(setor, lote, linhas),
    linhas,
    // Completa as 10 linhas para a caneta; nos setores com descrição as que
    // não couberem ficam escondidas pela própria tabela (nunca uma com dado).
    linhasEmBranco: Math.max(0, LINHAS_POR_FOLHA - linhas.length),
  };
}

function folhaDeCarregamento(lote) {
  return {
    chave: `${lote.chave}:${CARREGAMENTO.id}`,
    tipo: "carregamento",
    setor: CARREGAMENTO,
    lote,
    cliente: lote.cliente,
    ambientes: lote.ambientes.map((ambiente) => ambiente.nome),
  };
}

/**
 * Uma folha por lote e setor escolhido. O formulário de Carregamento tem 20
 * espaços para ambientes, então sai mesmo em lote acima do limite.
 *
 * @param selecao lista de { lote, setores: [ids] } na ordem em que os lotes
 *   devem sair. A ordem dos setores é sempre a do fluxo da fábrica.
 */
export function montarFolhas(selecao) {
  if (!Array.isArray(selecao)) throw new TypeError("montarFolhas espera uma lista de { lote, setores }.");
  return selecao.flatMap(({ lote, setores }) => {
    const escolhidos = new Set(setores);
    const deProducao = loteCabeNumaFolha(lote)
      ? SETORES.filter((setor) => escolhidos.has(setor.id) && setorSeAplica(setor, lote)).map((setor) => folhaDoSetor(setor, lote))
      : [];
    return escolhidos.has(CARREGAMENTO.id) ? [...deProducao, folhaDeCarregamento(lote)] : deProducao;
  });
}

/**
 * Degraus do ajuste de uma folha que não coube (decisão do Igor, 23/10):
 *   0 — desenho aprovado (as linhas em branco que não cabem já somem);
 *   1 — linhas mais baixas;
 *   2 — letra menor na tabela e nas descrições;
 *   3 — quadro de Observações menor.
 * Se nem o degrau 3 coube, a letra e as linhas da tabela diminuem na
 * proporção que falta (escala < 1; as colunas mantêm a largura) e a tela
 * avisa "letra reduzida". Abaixo de ESCALA_MINIMA a letra fica ilegível:
 * para ali e marca `naoCabe`, e a tela avisa para conferir o lote.
 */
export const ULTIMO_DEGRAU = 3;
export const ESCALA_MINIMA = 0.45;
export const AJUSTE_INICIAL = Object.freeze({ nivel: 0, escala: 1 });

export function proximoAjuste(atual, { alturaDoConteudo, alturaUtil }) {
  if (atual.naoCabe || !(alturaUtil > 0) || alturaDoConteudo <= alturaUtil) return atual;
  if (atual.nivel < ULTIMO_DEGRAU) return { nivel: atual.nivel + 1, escala: 1 };
  if (atual.escala <= ESCALA_MINIMA) return { ...atual, naoCabe: true };
  // Arredonda para baixo: garantir que cabe é melhor que 0,1% de letra.
  const proporcional = Math.floor((alturaUtil / alturaDoConteudo) * atual.escala * 1000) / 1000;
  return { nivel: atual.nivel, escala: Math.max(ESCALA_MINIMA, Math.min(proporcional, atual.escala - 0.01)) };
}
